import {useMemo, useState} from 'react';
import {Setting} from '@esgi/ui';
import {Text} from '@esgi/ui/typography';
import {Drawer} from '@esgi/main/kits/common';
import {SidebarMenu} from './components/settings-tab-menu';
import {GradeScales} from './components/tabs/grade-scales';
import {SystemSettings} from './components/tabs/system-settings';
import {TestButtonLabels} from './components/tabs/test-button-labels';
import {TestResultsVerbiage} from './components/tabs/test-results-verbiage';
import {StudentTestingOptions} from './components/tabs/student-testing-options';
import {LanguagesAndTemplates} from './components/tabs/languages-and-templates';
import {TestingKeyboardShortcuts} from './components/tabs/keyboard-shortcuts';
import {ExpandedTestSessionResults} from './components/tabs/test-session-results';
import {SettingsTab, OutModel} from '../../types';
import {SidebarHeader} from './index.styled';
import {tabItems} from './constants';

interface Props {
	initData: OutModel;
	onChangeDesignVersion: VoidFunction;
	onUpdateInitData: (props: Partial<OutModel>) => void;
}

export function Panel({initData, onChangeDesignVersion, onUpdateInitData}: Props) {
	const [selectedTab, setSelectedTab] = useState<SettingsTab>(
		SettingsTab.SystemSettings
	);

	const renderTabContent = useMemo(() => {
		switch (selectedTab) {
			case SettingsTab.SystemSettings:
				return (
					<SystemSettings
						timeZones={initData?.timeZones}
						sortBy={initData?.sortBy}
						sortByVariants={initData.sortByVariants}
						selectedTimeZone={initData.selectedTimeZone}
						isCleverAccountLinked={initData.isCleverAccountLinked}
						isOneClickAccountLinked={initData.isOneClickAccountLinked}
						onUpdateInitData={onUpdateInitData}
						onChangeDesignVersion={onChangeDesignVersion}
					/>
				);

			case SettingsTab.LanguagesAndTemplates:
				return <LanguagesAndTemplates />;

			case SettingsTab.GradeScales:
				return <GradeScales />;

			case SettingsTab.KeyboardShortcuts:
				return (
					<TestingKeyboardShortcuts
						isKeyboardShortcuts={initData.enableShortcuts}
						onUpdateInitData={onUpdateInitData}
					/>
				);

			case SettingsTab.TestButtonLabels:
				return (
					<TestButtonLabels
						correctTestLabel={initData.correctTestLabel}
						incorrectTestLabel={initData.incorrectTestLabel}
						testButtonsLabelsEnabled={initData.testButtonsLabelsEnabled}
						onUpdateInitData={onUpdateInitData}
					/>
				);

			case SettingsTab.StudentTestingOptions:
				return (
					<StudentTestingOptions
						lockScreen={initData.lockScreen}
						screenLockPIN={initData.screenLockPIN}
						showSelfAssessOption={initData.showSelfAssessOption}
						onUpdateInitData={onUpdateInitData}
					/>
				);

			case SettingsTab.TestResultVerbiage:
				return (
					<TestResultsVerbiage
						testResultsVerbiagesEnabled={initData.testResultsVerbiagesEnabled}
						testResultsCorrectVerbiage={initData.testResultsCorrectVerbiage}
						testResultsIncorrectVerbiage={initData.testResultsIncorrectVerbiage}
						onUpdateInitData={onUpdateInitData}
					/>
				);

			case SettingsTab.TestSessionResults:
				return (
					<ExpandedTestSessionResults showTestSessionResults={initData.showTestSessionResults} onUpdateInitData={onUpdateInitData}/>
				);
		}
	}, [selectedTab, initData, onUpdateInitData, onChangeDesignVersion]);

	return (
		<Drawer.Panel data-cy='settings-panel'>
			<Drawer.PanelSidebar data-cy='settings-tab-bar'>
				<SidebarHeader>
					<Setting />
					<Text size='small' color='neutral56' data-cy='settings-title'>
						Settings
					</Text>
				</SidebarHeader>
				<SidebarMenu
					selectedTab={selectedTab}
					onChange={setSelectedTab}
					tabItems={tabItems}
				/>
			</Drawer.PanelSidebar>
			{renderTabContent}
		</Drawer.Panel>
	);
}
